'use client';

import { type ComponentProps, createContext, forwardRef, useContext } from 'react';
import { m } from 'motion/react';
import { cx } from 'styled-system/css';
import { drawer } from 'styled-system/recipes';

import { backdropVariants, type DrawerSide, drawerVariants } from '@shaojiang/lib/motion';

import * as Styled from '../styled/drawer';
import {
  MotionPresenceProvider,
  onClosed,
  useMotionPresence,
  useMotionPresenceContext,
} from './presence';

// Everything not redefined below (Header, Body, Title…) is Park UI's as-is.
export * from '../styled/drawer';

const SideContext = createContext<DrawerSide>('right');

export type RootProps = Styled.RootProps;

/** Drawer root that keeps the panel mounted until it has slid out. */
export const Root = (props: RootProps) => {
  const { open, defaultOpen, onOpenChange, ...rest } = props;
  const presence = useMotionPresence({ open, defaultOpen, onOpenChange });
  const side: DrawerSide = props.variant === 'left' ? 'left' : 'right';
  return (
    <MotionPresenceProvider value={presence}>
      <SideContext.Provider value={side}>
        <Styled.Root
          {...rest}
          open={presence.open}
          present={presence.present}
          onOpenChange={presence.onOpenChange}
        />
      </SideContext.Provider>
    </MotionPresenceProvider>
  );
};

export interface BackdropProps {
  className?: string;
}

/** Our own backdrop (Ark's hides instantly on close) using the drawer recipe styles. */
export const Backdrop = forwardRef<HTMLDivElement, BackdropProps>(({ className }, ref) => {
  const { open, present } = useMotionPresenceContext();
  if (!present) return null;
  return (
    <m.div
      ref={ref}
      aria-hidden
      className={cx(drawer().backdrop, className)}
      variants={backdropVariants}
      initial='closed'
      animate={open ? 'open' : 'closed'}
    />
  );
});
Backdrop.displayName = 'DrawerBackdrop';

export type ContentProps = ComponentProps<typeof Styled.Content>;

/** Drawer panel: slides in from its side. */
export const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, ...props }, ref) => {
  const { open, onExitComplete } = useMotionPresenceContext();
  const side = useContext(SideContext);
  return (
    <Styled.Content ref={ref} {...props} asChild>
      <m.div
        custom={side}
        variants={drawerVariants}
        initial='closed'
        animate={open ? 'open' : 'closed'}
        onAnimationComplete={onClosed(onExitComplete)}
      >
        {children}
      </m.div>
    </Styled.Content>
  );
});
Content.displayName = 'DrawerContent';
